import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { iconFor } from "@/lib/contentIcons";
import { Check, FileWarning } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/common/PageHero";
import HelpCTA from "@/components/common/HelpCTA";
import EmptyState from "@/components/common/EmptyState";
import { Reveal } from "@/components/common/Reveal";
import { Skeleton } from "@/components/ui/skeleton";
import { servicesService } from "@/services/contentServices";
import { useSite } from "@/context/SiteDataContext";

export default function ServiceDetail() {
  const SITE = useSite();
  const { slug } = useParams();
  const [service, setService] = useState(undefined);

  useEffect(() => {
    setService(undefined);
    servicesService.getServiceBySlug(slug).then(setService);
  }, [slug]);

  if (service === undefined) {
    return <div className="container-x py-24 space-y-4"><Skeleton className="h-6 w-40" /><Skeleton className="h-14 w-2/3" /><Skeleton className="h-40 w-full" /></div>;
  }
  if (!service) {
    return <div className="container-x py-24"><EmptyState icon={FileWarning} title="Service not found" description="This service page is unavailable." actionLabel="All Services" actionTo="/services" /></div>;
  }

  const Icon = iconFor(service.icon);

  return (
    <>
      <Seo title={service.name} description={service.short} canonical={`https://${SITE.website}/services/${service.slug}`} />
      <PageHero eyebrow="Services" title={service.name} description={service.hero} crumbs={[{ label: "Services", to: "/services" }, { label: service.name }]}>
        <div className="mt-8 w-14 h-14 rounded-md bg-aqua text-white flex items-center justify-center"><Icon className="w-7 h-7" strokeWidth={1.7} /></div>
      </PageHero>

      <section className="container-x py-16 md:py-24">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-lg text-greyish leading-relaxed">{service.body}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="rounded-lg border border-border bg-[#f9fafb] p-8">
                <p className="eyebrow">What's included</p>
                <ul className="mt-5 space-y-3.5">
                  {service.highlights.map((h) => (
                    <li key={h} className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-aqua/15 flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3.5 h-3.5 text-aqua" /></span>
                      <span className="text-ink">{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
        {service.images?.length > 0 && (
          <Reveal>
            <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {service.images.map((src, i) => (
                <div
                  key={src}
                  className={`overflow-hidden rounded-lg bg-[#f6f8f9] ${
                    // The first image leads the gallery at double width.
                    i === 0 ? "sm:col-span-2 aspect-[16/9]" : "aspect-[4/3]"
                  }`}
                >
                  <img
                    src={src}
                    alt={`${service.name} ${i + 1}`}
                    loading="lazy"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
              ))}
            </div>
          </Reveal>
        )}
      </section>

      <HelpCTA
        eyebrow={SITE.keyQuestion}
        title={`Interested in ${service.name.toLowerCase()}?`}
        body="Talk to our team about how this can support your laboratory. Send an enquiry, contact us, or message us on WhatsApp."
        waMessage={`Hi Athena Scientific, I would like to enquire about ${service.name}.`}
      />
    </>
  );
}
